import { Space } from 'antd';
import React, { FC } from 'react';
import { FormattedMessage } from 'react-intl';
import { Link } from 'react-router-dom';
import styled, { css } from 'styled-components';
import {
  BackgroundColor,
  InputBorder,
  InputFocus,
} from 'components/atoms/input/input';
import { Layout } from 'components/atoms/layout';
import { PageLoading } from 'components/atoms/loading';
import {
  BodyShort01,
  Label01,
  ProductiveHeading04,
  Text01,
} from 'components/styles';
import { RoutePaths } from 'constant';
import { useAccountMetaQuery } from 'queries';
import { CompanyLogo } from 'routes/auth/common/company-logo';
import { lazyWithRetry } from 'utils/lazyWithRetry';
import { useIsSamlMandatory } from '../saml';
import './login.css';

const OktaSignInWidget = lazyWithRetry(() =>
  import('./okta-signin-widget').then(({ SignInWidget }) => ({
    default: SignInWidget,
  })),
);

const { Content } = Layout;

const Container = styled(Content)`
  display: flex;
  flex-direction: column;
  height: 100vh;
  padding: 120px;
  align-items: flex-start;
`;

const Heading = styled.div`
  ${ProductiveHeading04};
`;

const HelperLink = styled(Link)`
  ${BodyShort01};
`;

const StyledSignInWidget = styled(OktaSignInWidget)<{
  isSamlEnabled: boolean;
}>`
  margin-top: var(--layout-04);
  #okta-sign-in {
    &.auth-container {
      &.main-container {
        box-shadow: none;
        border: none;
        margin-top: 0;
      }
      * {
        font-family: var(--font-family) !important;
      }
    }
    .auth-content {
      padding: 0;
    }
    .auth-footer {
      display: none;
    }
    label {
      ${Label01};
    }
    .o-form .okta-form-input-field {
      margin: 0;
      width: 100%;
      padding: 0 var(--spacing-05);
      ${InputBorder};
      ${BodyShort01};
      ${Text01};
      ${BackgroundColor};
      height: var(--input-height-lg);

      &.focused-input {
        ${InputFocus};
      }
    }
    /* For signin button*/
    #okta-signin-submit.button.button-primary {
      text-align: left;
      border-radius: 0;
      ${({ isSamlEnabled }) =>
        isSamlEnabled &&
        css`
          background-color: transparent;
          border-color: var(--blue-60);
          color: var(--blue-60);
        `};
    }
  }

  #okta-sign-in .focused-input {
    box-shadow: none;
  }
  #okta-sign-in .social-auth-button {
    padding: 0 0 0 var(--spacing-05);
    background: var(--blue-60);
    color: var(--white-100);
  }
`;

export const LoginPage: FC = () => {
  const { data, isLoading, isError } = useAccountMetaQuery();
  const isSamlMandate = useIsSamlMandatory();

  return isLoading ? (
    <PageLoading />
  ) : data && !isError ? (
    <Layout>
      <Container>
        <Space direction="vertical" size={16}>
          {data && <CompanyLogo data={data.company} />}
          <Space>
            <Heading>
              <FormattedMessage
                id="login.title"
                values={{
                  companyName: data.company.companyName,
                }}
              />
            </Heading>
            <Heading>
              <strong>
                <FormattedMessage id="onboarding" />
              </strong>
            </Heading>
          </Space>
        </Space>
        <StyledSignInWidget
          oktaMetaData={data.okta}
          isSamlMandatory={isSamlMandate}
          isSamlEnabled={
            !!(data.okta.idps ?? []).filter((idp) => idp.enabled).length
          }
        />
        {!isSamlMandate ? (
          <HelperLink to={RoutePaths.forgotPassword}>
            <FormattedMessage id="forgot_password" />
          </HelperLink>
        ) : null}
      </Container>
    </Layout>
  ) : (
    <></>
  );
};
